import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {assert} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PIPELINE_STEP_SHAPES} from 'pipelines/GeneratedPipelineStepShapes';
import useStepTypeInfos from 'pipelines/hooks/useStepTypeInfosQuery';
import {validateSingleInputPipelineStepConfig} from 'pipelines/step-config-validation/validatePipelineStepConfig';
import {StepConfigShape} from 'pipelines/StepShapeUtils';
import {getUserFacingStepTypeName} from 'pipelines/StepTypeInfoUtils';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import InlineBanner from 'ui-library/banners/InlineBanner';
import Modal from 'ui-library/modals/Modal';
import {JSONValue} from 'util/Types';

import makeInitialConfigState from './initial-values/makeInitialConfigState';
import StepConfigEditorPane from './StepConfigEditorPane';
import {ConfigEditorStepType, stepTypeIsConfigEditorSupported} from './stepTypeIsConfigEditorSupported';

/** @package */
export const UPDATES_SYSTEM_COLUMN_NAMES_WARNING_BANNER_TEXT =
  'This transformation edits existing columns, which may cause transforms that reference those columns to error.  You can fix this by editing the column references downstream.';

const UPDATES_SYSTEM_COLUMN_NAMES_STEP_TYPES = [
  PipelineStepType.AGGREGATE_DATASET,
  PipelineStepType.BIN_DATA,
  PipelineStepType.COMPUTED_COLUMN,
  PipelineStepType.HIDE_COLUMNS,
  PipelineStepType.PIVOT_DATASET,
  PipelineStepType.UNPIVOT_DATASET,
  PipelineStepType.WINDOW_FUNCTIONS,
] as const satisfies ReadonlyArray<ConfigEditorStepType>;

type UpdatesSystemColumnNamesStepType = (typeof UPDATES_SYSTEM_COLUMN_NAMES_STEP_TYPES)[number];

function isUpdatesSystemColumnNamesStepType(
  stepType: ConfigEditorStepType
): stepType is UpdatesSystemColumnNamesStepType {
  // @ts-expect-error
  return UPDATES_SYSTEM_COLUMN_NAMES_STEP_TYPES.includes(stepType);
}

export interface StepConfigModalProps {
  stepType: ConfigEditorStepType;
  stepVersion: number;
  initialConfig?: Record<string, JSONValue>;
  inputDataFrameSpec: DataFrameSpec;
  onRequestClose: () => void;
  onRequestSubmit: (config: Record<string, JSONValue>) => void;
  stepIsFailed?: boolean;
  /** The optional props below are defined by InputAwareStepConfigEditorModal */
  error?: string;
  inputIsComplete?: boolean;
  renderStepInputComponent?: (
    config: Record<string, JSONValue>,
    setConfig: (newConfig: Record<string, JSONValue>) => void
  ) => React.ReactNode;
  readOnly?: boolean;
  clientStateId: string;
  step?: GetAnalysisPipelineStep | null;
}

export default function StepConfigEditorModal({
  stepType,
  stepVersion,
  initialConfig,
  inputDataFrameSpec,
  onRequestClose,
  onRequestSubmit,
  stepIsFailed,
  error,
  clientStateId,
  inputIsComplete = true,
  renderStepInputComponent,
  readOnly = false,
  step,
}: StepConfigModalProps): JSX.Element {
  const stepTypeInfos = useStepTypeInfos();
  const configVariablesShape: StepConfigShape | null =
    PIPELINE_STEP_SHAPES[stepType]?.[stepVersion]?.config ?? null;

  assert(
    stepTypeIsConfigEditorSupported(stepType, stepVersion) && configVariablesShape != null,
    `Config shape should not be null for stepType ${stepType} and stepVersion ${stepVersion}`
  );

  const modalTitle = getUserFacingStepTypeName(stepType, stepTypeInfos);
  const [config, setConfig] = useState<Record<string, JSONValue>>(
    initialConfig ?? makeInitialConfigState(stepType, stepVersion)
  );

  const {isComplete, errors} = validateSingleInputPipelineStepConfig(
    stepType,
    stepVersion,
    config,
    inputDataFrameSpec
  );

  return (
    <Modal
      buttons={[
        {
          submit: true,
          text: 'Submit',
          disabled: !inputIsComplete || !isComplete || errors.length > 0 || readOnly,
        },
      ]}
      dataTestAttrs={{component: 'StepConfigEditorModal', element: 'root'}}
      expandHeight={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit(config);
      }}
      scrollable={true}
      title={modalTitle}
    >
      {isUpdatesSystemColumnNamesStepType(stepType) && initialConfig != null && (
        <div className={css(styles.banner)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={UPDATES_SYSTEM_COLUMN_NAMES_WARNING_BANNER_TEXT}
          />
        </div>
      )}
      {renderStepInputComponent != null && renderStepInputComponent(config, setConfig)}
      <StepConfigEditorPane
        stepType={stepType}
        stepVersion={stepVersion}
        config={config}
        setConfig={setConfig}
        dataFramesInfos={{[SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [{dataFrameSpec: inputDataFrameSpec}]}}
        stepIsFailed={stepIsFailed}
        error={error}
        readOnly={readOnly}
        clientStateId={clientStateId}
        stepId={step ? step.id : undefined}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  banner: {
    paddingBottom: 8,
  },
});
